/**
 * MassTriageRoom — sala de urgencias simulada con N pacientes llegando
 * simultáneamente. El alumno debe clasificarlos (rojo/ámbar/verde/negro)
 * rápido, replicando un ejercicio de triage START real.
 *
 * Mecánica pedagógica:
 *   1. Pacientes aparecen como cards con vitales en vivo y tiempo de espera.
 *   2. Algunos se deterioran si no se priorizan a tiempo (ej. IAM: PAS baja).
 *   3. Alumno asigna color → scoring diferencial:
 *      - Correcto → +20
 *      - Over-triage (sobreclasificó) → -5
 *      - Sub-triage (subestimó gravedad) → -30 (error clínicamente grave)
 *      - No clasificó a tiempo → 0 + el paciente puede morir
 *   4. Al terminar: tabla con resultado por paciente + score total + rationale.
 *
 * Esto es lo que los fantomas físicos NO hacen: N pacientes en paralelo,
 * scoring granular, deterioro dinámico, y feedback clínico explícito.
 */

import type { TriagePriority } from '../types/medical';
import { samplePool, type MassTriagePatientTemplate } from './MassTriagePool';
import { recordAttempt } from '../auth/ProgressSync';
import { authService } from '../auth/AuthService';

interface AIDebrief {
  summary: string;
  strengths: string[];
  opportunities: string[];
  clinical_notes: string[];
}

const PANEL_ID = 'cgt-mass-triage';
const STYLE_ID = 'cgt-mass-triage-styles';

interface LivePatient extends MassTriagePatientTemplate {
  uid: string;
  /** Estado actual de los vitales (muta con deterioro). */
  currentVitals: MassTriagePatientTemplate['vitals'];
  /** Tiempo transcurrido desde que llegó (segundos). */
  waitedSec: number;
  /** Clasificación elegida por el alumno. */
  assigned: TriagePriority | null;
  /** Cuándo el alumno clasificó. */
  assignedAtSec: number | null;
}

const TRIAGE_BUTTONS: Array<{ value: TriagePriority; label: string; color: string; key: string }> = [
  { value: 'immediate', label: 'INMEDIATO', color: '#FF453A', key: '1' },
  { value: 'delayed', label: 'PUEDE ESPERAR', color: '#FF9F0A', key: '2' },
  { value: 'minor', label: 'AMBULATORIO', color: '#30D158', key: '3' },
  { value: 'expectant', label: 'EXPECTANTE', color: '#8E8E93', key: '4' },
];

/** Orden numérico para comparar gravedad (mayor = más grave). */
const TRIAGE_RANK: Record<TriagePriority, number> = {
  deceased: 0,
  expectant: 1,
  minor: 2,
  delayed: 3,
  immediate: 4,
};

export class MassTriageRoom {
  private root: HTMLElement | null = null;
  private patients: LivePatient[] = [];
  private tickTimer: number | null = null;
  private startedAt = 0;
  /** Tiempo máximo antes de auto-terminar el ejercicio (segundos). */
  private maxDurationSec = 300;
  private onCloseCallback: (() => void) | null = null;

  open(n = 6, onClose?: () => void): void {
    if (this.root) return;
    this.onCloseCallback = onClose ?? null;
    this.injectStyles();
    // Llegadas escalonadas: distribuir waitedSec inicial entre 0 y 420s (7 min)
    // para que no todos los pacientes muestren el mismo tiempo "en sala".
    // Refleja la realidad de urgencias: algunos llevan esperando, otros recién
    // llegan. Fuerza al alumno a priorizar también por tiempo, no solo vitales.
    const sampled = samplePool(n);
    this.patients = sampled.map((t, idx) => ({
      ...t,
      uid: `${t.id}-${Math.random().toString(36).slice(2, 6)}`,
      currentVitals: { ...t.vitals },
      // Staggered: 0, ~70s, ~140s, ~210s... más algo aleatorio para variedad
      waitedSec: Math.floor(idx * 70 + Math.random() * 40),
      assigned: null,
      assignedAtSec: null,
    }));
    this.startedAt = Date.now();

    const root = document.createElement('div');
    root.id = PANEL_ID;
    root.innerHTML = `
      <header class="cgt-mt-header">
        <div>
          <h1>🚑 Sala de urgencias — Mass triage</h1>
          <p>Clasificá a todos los pacientes lo más rápido posible. El tiempo corre y algunos se deterioran.</p>
        </div>
        <div class="cgt-mt-stats">
          <div class="cgt-mt-timer" data-timer>5:00</div>
          <div class="cgt-mt-progress" data-progress>0 / ${this.patients.length} clasificados</div>
          <button type="button" class="cgt-mt-finish" data-finish>Finalizar ahora</button>
          <button type="button" class="cgt-mt-close" data-close aria-label="Cerrar">×</button>
        </div>
      </header>
      <div class="cgt-mt-grid" data-grid></div>
    `;
    document.body.appendChild(root);
    requestAnimationFrame(() => root.classList.add('cgt-mt-visible'));
    this.root = root;

    root.querySelector<HTMLButtonElement>('[data-close]')?.addEventListener('click', () => this.close());
    root.querySelector<HTMLButtonElement>('[data-finish]')?.addEventListener('click', () => this.finish());

    this.renderGrid();
    this.startTicker();
  }

  close(): void {
    if (!this.root) return;
    this.stopTicker();
    const el = this.root;
    el.classList.remove('cgt-mt-visible');
    setTimeout(() => el.remove(), 200);
    this.root = null;
    const cb = this.onCloseCallback;
    this.onCloseCallback = null;
    cb?.();
  }

  private startTicker(): void {
    this.tickTimer = window.setInterval(() => this.tick(), 1000);
  }
  private stopTicker(): void {
    if (this.tickTimer !== null) {
      window.clearInterval(this.tickTimer);
      this.tickTimer = null;
    }
  }

  private tick(): void {
    const elapsed = Math.round((Date.now() - this.startedAt) / 1000);
    const remaining = this.maxDurationSec - elapsed;
    if (remaining <= 0) {
      this.finish();
      return;
    }
    // Timer display
    const tm = this.root?.querySelector<HTMLElement>('[data-timer]');
    if (tm) {
      const m = Math.floor(remaining / 60);
      const s = remaining % 60;
      tm.textContent = `${m}:${String(s).padStart(2, '0')}`;
      tm.classList.toggle('cgt-mt-timer-warn', remaining < 60);
    }

    // Update patients: deterioro si no clasificados
    this.patients.forEach(p => {
      p.waitedSec += 1;
      if (p.assigned === null && p.deteriorationPerMin) {
        const perSec = 1 / 60;
        const d = p.deteriorationPerMin;
        if (d.hr !== undefined) p.currentVitals.hr += d.hr * perSec;
        if (d.sbp !== undefined) p.currentVitals.sbp += d.sbp * perSec;
        if (d.spo2 !== undefined) p.currentVitals.spo2 += d.spo2 * perSec;
        if (d.rr !== undefined) p.currentVitals.rr += d.rr * perSec;
      }
    });
    this.updateGridLive();
  }

  private renderGrid(): void {
    const grid = this.root?.querySelector<HTMLElement>('[data-grid]');
    if (!grid) return;
    grid.innerHTML = this.patients.map(p => this.renderCard(p)).join('');
    // Listeners de botones de triage
    grid.querySelectorAll<HTMLButtonElement>('[data-assign]').forEach(btn => {
      btn.addEventListener('click', () => {
        const uid = btn.dataset.uid!;
        const value = btn.dataset.assign as TriagePriority;
        this.assign(uid, value);
      });
    });
  }

  /** Re-renderiza sólo los valores dinámicos (vitales + tiempo) sin recrear DOM. */
  private updateGridLive(): void {
    this.patients.forEach(p => {
      const card = this.root?.querySelector<HTMLElement>(`[data-uid="${p.uid}"]`);
      if (!card) return;
      const vitals = card.querySelector<HTMLElement>('[data-vitals]');
      const wait = card.querySelector<HTMLElement>('[data-wait]');
      if (vitals) vitals.innerHTML = this.vitalsHTML(p);
      if (wait) {
        const m = Math.floor(p.waitedSec / 60);
        const s = p.waitedSec % 60;
        wait.textContent = `${m}:${String(s).padStart(2, '0')}`;
      }
    });
  }

  private renderCard(p: LivePatient): string {
    const m = Math.floor(p.waitedSec / 60);
    const s = p.waitedSec % 60;
    const waitStr = `${m}:${String(s).padStart(2, '0')}`;
    const isAssigned = p.assigned !== null;
    const assignedColor = p.assigned ? TRIAGE_BUTTONS.find(b => b.value === p.assigned)?.color : null;
    return `
      <article class="cgt-mt-card ${isAssigned ? 'cgt-mt-card-done' : ''}" data-uid="${p.uid}" style="${assignedColor ? `--assigned:${assignedColor}` : ''}">
        <header>
          <span class="cgt-mt-icon">${p.icon}</span>
          <div class="cgt-mt-patient-info">
            <strong>${esc(p.name)}</strong>
            <small>${p.age} años · ${p.sex === 'M' ? 'M' : 'F'}</small>
          </div>
          <div class="cgt-mt-wait">
            <small>en sala</small>
            <span data-wait>${waitStr}</span>
          </div>
        </header>
        <p class="cgt-mt-cc">${esc(p.chiefComplaint)}</p>
        <div class="cgt-mt-vitals" data-vitals>${this.vitalsHTML(p)}</div>
        <div class="cgt-mt-actions">
          ${TRIAGE_BUTTONS.map(b => `
            <button type="button" data-assign="${b.value}" data-uid="${p.uid}"
              class="cgt-mt-btn ${p.assigned === b.value ? 'cgt-mt-btn-active' : ''}"
              style="--accent:${b.color}"
              ${isAssigned ? 'disabled' : ''}
            >${esc(b.label)}</button>
          `).join('')}
        </div>
      </article>
    `;
  }

  private vitalsHTML(p: LivePatient): string {
    const v = p.currentVitals;
    const hrAlarm = v.hr < 60 || v.hr > 100 ? 'warn' : 'ok';
    const sbpAlarm = v.sbp < 90 ? 'crit' : v.sbp > 140 ? 'warn' : 'ok';
    const spo2Alarm = v.spo2 < 90 ? 'crit' : v.spo2 < 95 ? 'warn' : 'ok';
    const rrAlarm = v.rr < 12 || v.rr > 20 ? 'warn' : 'ok';
    return `
      <span class="cgt-mt-v cgt-mt-v-${hrAlarm}">FC <strong>${Math.round(v.hr)}</strong></span>
      <span class="cgt-mt-v cgt-mt-v-${sbpAlarm}">PA <strong>${Math.round(v.sbp)}/${Math.round(v.dbp)}</strong></span>
      <span class="cgt-mt-v cgt-mt-v-${spo2Alarm}">SpO₂ <strong>${Math.round(v.spo2)}%</strong></span>
      <span class="cgt-mt-v cgt-mt-v-${rrAlarm}">FR <strong>${Math.round(v.rr)}</strong></span>
      ${v.glasgow !== undefined ? `<span class="cgt-mt-v cgt-mt-v-${v.glasgow < 13 ? 'crit' : 'ok'}">GCS <strong>${v.glasgow}</strong></span>` : ''}
      ${v.pain !== undefined ? `<span class="cgt-mt-v cgt-mt-v-ok">Dolor <strong>${v.pain}/10</strong></span>` : ''}
    `;
  }

  private assign(uid: string, value: TriagePriority): void {
    const p = this.patients.find(x => x.uid === uid);
    if (!p || p.assigned !== null) return;
    p.assigned = value;
    p.assignedAtSec = Math.round((Date.now() - this.startedAt) / 1000);
    // Re-renderiza solo esa card
    this.renderGrid();
    this.updateProgress();
    // Si ya clasificó todos, ir directo al resultado
    if (this.patients.every(x => x.assigned !== null)) {
      setTimeout(() => this.finish(), 400);
    }
  }

  private updateProgress(): void {
    const el = this.root?.querySelector<HTMLElement>('[data-progress]');
    if (!el) return;
    const done = this.patients.filter(p => p.assigned !== null).length;
    el.textContent = `${done} / ${this.patients.length} clasificados`;
  }

  // ─────────────────────────────────────────────────────────
  // Finalizar y mostrar resultados
  // ─────────────────────────────────────────────────────────

  private finish(): void {
    this.stopTicker();
    const totalSec = Math.round((Date.now() - this.startedAt) / 1000);

    // Score por paciente
    interface Scored {
      patient: LivePatient;
      score: number;
      kind: 'correct' | 'over' | 'sub' | 'missed';
      diff: string;
    }
    const scored: Scored[] = this.patients.map(p => {
      if (p.assigned === null) {
        return {
          patient: p, score: 0, kind: 'missed',
          diff: `No clasificado. Correcto: ${this.triageLabel(p.expected)}`,
        };
      }
      if (p.assigned === p.expected) {
        return {
          patient: p, score: 20, kind: 'correct',
          diff: `Clasificación correcta.`,
        };
      }
      const over = TRIAGE_RANK[p.assigned] > TRIAGE_RANK[p.expected];
      if (over) {
        return {
          patient: p, score: -5, kind: 'over',
          diff: `Sobre-clasificado: asignaste ${this.triageLabel(p.assigned)} y era ${this.triageLabel(p.expected)}. Mal uso de recurso crítico.`,
        };
      }
      return {
        patient: p, score: -30, kind: 'sub',
        diff: `⚠️ Sub-triage: asignaste ${this.triageLabel(p.assigned)} y era ${this.triageLabel(p.expected)}. Clínicamente grave — paciente podría descompensarse sin atención oportuna.`,
      };
    });

    const rawTotal = scored.reduce((a, s) => a + s.score, 0);
    const maxPossible = this.patients.length * 20;
    const normalized = Math.max(0, Math.min(100, Math.round((rawTotal / maxPossible) * 100)));

    // Persistir intento (si está autenticado)
    void recordAttempt({
      case_id: `mass-triage-${this.patients.length}`,
      diagnosis_correct: scored.every(s => s.kind === 'correct'),
      triage_correct: scored.filter(s => s.kind === 'correct').length >= Math.ceil(this.patients.length * 0.7),
      score: normalized,
      duration_seconds: totalSec,
    }).catch(() => { /* silent */ });

    this.renderResults(scored, normalized, totalSec);
    // Lanzar el debrief IA en paralelo al render. No bloquea la UI.
    void this.fetchAIDebrief(scored, normalized, totalSec);
  }

  /**
   * Llama al backend para generar un reporte personalizado con Kimi.
   * El análisis detecta patrones (sobre-clasificación, fallos en reconocer
   * shock compensado, tiempo por paciente, etc). Este coaching personalizado
   * es LO QUE LOS FANTOMAS FÍSICOS NO PUEDEN HACER.
   */
  private async fetchAIDebrief(
    scored: Array<{ patient: LivePatient; score: number; kind: string; diff: string }>,
    normalized: number,
    totalSec: number,
  ): Promise<void> {
    if (!authService.isAuthenticated) return;
    const payload = {
      patients: scored.map(s => ({
        name: s.patient.name,
        age: s.patient.age,
        sex: s.patient.sex,
        chiefComplaint: s.patient.chiefComplaint,
        initial_vitals: s.patient.vitals,
        final_vitals: s.patient.currentVitals,
        expected_triage: s.patient.expected,
        assigned_triage: s.patient.assigned,
        assigned_at_sec: s.patient.assignedAtSec,
        result_kind: s.kind,
      })),
      total_sec: totalSec,
      normalized_score: normalized,
    };
    try {
      const debrief = await authService.request<AIDebrief>('/ai/triage-debrief', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      this.renderAIDebrief(debrief);
    } catch (e) {
      console.warn('[mass-triage] debrief IA falló:', e);
      this.renderAIDebriefError(e instanceof Error ? e.message : String(e));
    }
  }

  private renderAIDebrief(debrief: AIDebrief): void {
    const holder = this.root?.querySelector<HTMLElement>('[data-ai-debrief]');
    if (!holder) return;
    holder.innerHTML = `
      <div class="cgt-mt-debrief-card">
        <div class="cgt-mt-debrief-head">
          <span class="cgt-mt-debrief-badge">🧠 Análisis del instructor IA</span>
        </div>
        <p class="cgt-mt-debrief-summary">${esc(debrief.summary)}</p>
        ${debrief.strengths.length ? `
          <div class="cgt-mt-debrief-section">
            <h4>✅ Fortalezas</h4>
            <ul>${debrief.strengths.map(s => `<li>${esc(s)}</li>`).join('')}</ul>
          </div>
        ` : ''}
        ${debrief.opportunities.length ? `
          <div class="cgt-mt-debrief-section">
            <h4>⚠️ Áreas a mejorar</h4>
            <ul>${debrief.opportunities.map(s => `<li>${esc(s)}</li>`).join('')}</ul>
          </div>
        ` : ''}
        ${debrief.clinical_notes.length ? `
          <div class="cgt-mt-debrief-section">
            <h4>🩺 Observaciones clínicas</h4>
            <ul>${debrief.clinical_notes.map(s => `<li>${esc(s)}</li>`).join('')}</ul>
          </div>
        ` : ''}
      </div>
    `;
  }

  private renderAIDebriefError(err: string): void {
    const holder = this.root?.querySelector<HTMLElement>('[data-ai-debrief]');
    if (!holder) return;
    holder.innerHTML = `
      <div class="cgt-mt-debrief-error">
        No se pudo generar el análisis del instructor IA: ${esc(err)}
      </div>
    `;
  }

  private renderResults(scored: Array<{ patient: LivePatient; score: number; kind: 'correct' | 'over' | 'sub' | 'missed'; diff: string }>, normalized: number, totalSec: number): void {
    if (!this.root) return;
    const counts = {
      correct: scored.filter(s => s.kind === 'correct').length,
      over: scored.filter(s => s.kind === 'over').length,
      sub: scored.filter(s => s.kind === 'sub').length,
      missed: scored.filter(s => s.kind === 'missed').length,
    };
    const kindEmoji = { correct: '✅', over: '🟠', sub: '🔴', missed: '⏱️' };
    const kindLabel = {
      correct: 'Correcto',
      over: 'Sobre-clasificado',
      sub: 'Sub-triage (grave)',
      missed: 'No clasificado a tiempo',
    };

    this.root.innerHTML = `
      <header class="cgt-mt-header">
        <div>
          <h1>📋 Resultado — Mass triage</h1>
          <p>Duración: ${Math.floor(totalSec / 60)}:${String(totalSec % 60).padStart(2, '0')}</p>
        </div>
        <button type="button" class="cgt-mt-close" data-close aria-label="Cerrar">×</button>
      </header>
      <div class="cgt-mt-result-body">
        <div class="cgt-mt-result-total">
          <div class="cgt-mt-result-num">${normalized}<span>/100</span></div>
          <div class="cgt-mt-result-counts">
            <span class="cgt-mt-ok">✅ ${counts.correct}</span>
            <span class="cgt-mt-warn">🟠 ${counts.over}</span>
            <span class="cgt-mt-crit">🔴 ${counts.sub}</span>
            <span class="cgt-mt-miss">⏱️ ${counts.missed}</span>
          </div>
          <small>Correctos · Sobre-clasificados · Sub-triage (grave) · No clasificados</small>
        </div>
        <ul class="cgt-mt-result-list">
          ${scored.map(s => `
            <li class="cgt-mt-result-row cgt-mt-row-${s.kind}">
              <div class="cgt-mt-result-head">
                <span>${kindEmoji[s.kind]}</span>
                <strong>${esc(s.patient.name)}</strong>
                <small>${esc(kindLabel[s.kind])}</small>
                <span class="cgt-mt-pts">${s.score > 0 ? '+' : ''}${s.score}</span>
              </div>
              <p class="cgt-mt-cc">${esc(s.patient.chiefComplaint)}</p>
              <p class="cgt-mt-rationale"><strong>¿Por qué?</strong> ${esc(s.patient.rationale)}</p>
              <p class="cgt-mt-diff">${esc(s.diff)}</p>
            </li>
          `).join('')}
        </ul>

        <div class="cgt-mt-debrief-holder" data-ai-debrief>
          <div class="cgt-mt-debrief-loading">
            🧠 Generando análisis personalizado del instructor IA… (20-40s)
          </div>
        </div>

        <div class="cgt-mt-result-actions">
          <button type="button" class="cgt-mt-btn-primary" data-replay>Otra ronda</button>
          <button type="button" class="cgt-mt-btn-secondary" data-close>Cerrar</button>
        </div>
      </div>
    `;
    this.root.querySelector<HTMLButtonElement>('[data-replay]')?.addEventListener('click', () => {
      const n = this.patients.length;
      this.close();
      setTimeout(() => this.open(n), 250);
    });
    this.root.querySelectorAll<HTMLButtonElement>('[data-close]').forEach(el =>
      el.addEventListener('click', () => this.close()),
    );
  }

  private triageLabel(v: TriagePriority): string {
    return TRIAGE_BUTTONS.find(b => b.value === v)?.label ?? v;
  }

  private injectStyles(): void {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      #${PANEL_ID} {
        position: fixed; inset: 0; z-index: 13800;
        background: #0a0a0a;
        color: #f5f5f7;
        font-family: -apple-system, BlinkMacSystemFont, 'Inter', sans-serif;
        display: flex; flex-direction: column;
        opacity: 0; transition: opacity 0.25s;
        overflow: hidden;
      }
      #${PANEL_ID}.cgt-mt-visible { opacity: 1; }
      #${PANEL_ID} .cgt-mt-header {
        display: flex; justify-content: space-between; align-items: center;
        padding: 16px 24px;
        background: linear-gradient(180deg, #111 0%, #0a0a0a 100%);
        border-bottom: 0.5px solid rgba(255,255,255,0.08);
        flex-shrink: 0;
      }
      #${PANEL_ID} .cgt-mt-header h1 { font-size: 18px; font-weight: 700; letter-spacing: -0.3px; }
      #${PANEL_ID} .cgt-mt-header p { font-size: 12px; color: #a1a1a6; margin-top: 4px; }
      #${PANEL_ID} .cgt-mt-stats {
        display: flex; gap: 14px; align-items: center;
      }
      #${PANEL_ID} .cgt-mt-timer {
        font-family: 'JetBrains Mono', monospace;
        font-size: 24px; font-weight: 800; color: #30D158;
        letter-spacing: 1px;
      }
      #${PANEL_ID} .cgt-mt-timer-warn { color: #FF453A; animation: cgt-mt-pulse 1s infinite; }
      @keyframes cgt-mt-pulse {
        50% { opacity: 0.5; }
      }
      #${PANEL_ID} .cgt-mt-progress {
        font-size: 11.5px; color: #8e8e93; text-transform: uppercase; letter-spacing: 1px;
      }
      #${PANEL_ID} .cgt-mt-finish, #${PANEL_ID} .cgt-mt-close {
        background: #2a2a2a; color: #fff; border: 0.5px solid rgba(255,255,255,0.15);
        padding: 8px 14px; border-radius: 8px; cursor: pointer;
        font-family: inherit; font-size: 12px; font-weight: 600;
      }
      #${PANEL_ID} .cgt-mt-close { padding: 6px 12px; font-size: 16px; line-height: 1; }
      #${PANEL_ID} .cgt-mt-finish:hover, #${PANEL_ID} .cgt-mt-close:hover { background: #3a3a3a; }

      #${PANEL_ID} .cgt-mt-grid {
        flex: 1; overflow-y: auto;
        padding: 16px 24px;
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(330px, 1fr));
        gap: 12px;
      }

      #${PANEL_ID} .cgt-mt-card {
        background: #141414;
        border: 0.5px solid rgba(255,255,255,0.08);
        border-radius: 12px;
        padding: 14px 16px;
        display: flex; flex-direction: column; gap: 10px;
        transition: border-color 0.2s;
      }
      #${PANEL_ID} .cgt-mt-card-done {
        border-left: 4px solid var(--assigned, #2997FF);
        opacity: 0.65;
      }
      #${PANEL_ID} .cgt-mt-card header {
        display: flex; align-items: center; gap: 10px; padding: 0; border: none; background: none;
      }
      #${PANEL_ID} .cgt-mt-icon { font-size: 28px; }
      #${PANEL_ID} .cgt-mt-patient-info { flex: 1; min-width: 0; }
      #${PANEL_ID} .cgt-mt-patient-info strong {
        display: block; font-size: 14px; color: #f5f5f7;
      }
      #${PANEL_ID} .cgt-mt-patient-info small {
        font-size: 11px; color: #8e8e93;
      }
      #${PANEL_ID} .cgt-mt-wait {
        text-align: right; font-family: 'JetBrains Mono', monospace;
      }
      #${PANEL_ID} .cgt-mt-wait small {
        display: block; font-size: 9px; color: #6e6e73;
        text-transform: uppercase; letter-spacing: 0.5px;
      }
      #${PANEL_ID} .cgt-mt-wait span {
        font-size: 14px; color: #FFD60A; font-weight: 700;
      }

      #${PANEL_ID} .cgt-mt-cc {
        font-size: 12px; color: #d1d1d6; line-height: 1.45;
      }

      #${PANEL_ID} .cgt-mt-vitals {
        display: flex; flex-wrap: wrap; gap: 6px;
      }
      #${PANEL_ID} .cgt-mt-v {
        padding: 4px 10px; border-radius: 14px;
        font-size: 11px; font-weight: 500;
        font-family: 'JetBrains Mono', monospace;
        letter-spacing: 0.3px;
      }
      #${PANEL_ID} .cgt-mt-v strong { font-weight: 700; margin-left: 2px; }
      #${PANEL_ID} .cgt-mt-v-ok {
        background: rgba(255,255,255,0.04); color: #d1d1d6;
      }
      #${PANEL_ID} .cgt-mt-v-warn {
        background: rgba(255,159,10,0.12); color: #FFD60A;
      }
      #${PANEL_ID} .cgt-mt-v-crit {
        background: rgba(255,69,58,0.15); color: #FF6961;
        animation: cgt-mt-vcrit 1.2s ease-in-out infinite;
      }
      @keyframes cgt-mt-vcrit {
        50% { background: rgba(255,69,58,0.3); }
      }

      #${PANEL_ID} .cgt-mt-actions {
        display: grid; grid-template-columns: repeat(4, 1fr); gap: 4px;
      }
      #${PANEL_ID} .cgt-mt-btn {
        background: rgba(255,255,255,0.03);
        border: 0.5px solid var(--accent);
        color: var(--accent);
        padding: 8px 4px; border-radius: 8px;
        font-family: inherit; font-weight: 700; font-size: 10px;
        letter-spacing: 0.3px; cursor: pointer;
        transition: background 0.15s;
      }
      #${PANEL_ID} .cgt-mt-btn:hover:not(:disabled) {
        background: color-mix(in srgb, var(--accent) 20%, transparent);
      }
      #${PANEL_ID} .cgt-mt-btn-active {
        background: var(--accent);
        color: #fff;
      }
      #${PANEL_ID} .cgt-mt-btn:disabled { cursor: default; opacity: 0.5; }

      /* Resultados */
      #${PANEL_ID} .cgt-mt-result-body {
        flex: 1; overflow-y: auto; padding: 24px;
        display: flex; flex-direction: column; gap: 20px;
      }
      #${PANEL_ID} .cgt-mt-result-total {
        text-align: center;
        padding: 24px;
        background: #141414; border: 0.5px solid rgba(255,255,255,0.08);
        border-radius: 14px;
      }
      #${PANEL_ID} .cgt-mt-result-num {
        font-size: 64px; font-weight: 800; letter-spacing: -2px; color: #f5f5f7;
        line-height: 1;
      }
      #${PANEL_ID} .cgt-mt-result-num span { font-size: 24px; color: #6e6e73; margin-left: 6px; }
      #${PANEL_ID} .cgt-mt-result-counts {
        display: flex; justify-content: center; gap: 20px; margin: 14px 0 6px;
        font-family: 'JetBrains Mono', monospace; font-size: 14px; font-weight: 700;
      }
      #${PANEL_ID} .cgt-mt-ok { color: #30D158; }
      #${PANEL_ID} .cgt-mt-warn { color: #FF9F0A; }
      #${PANEL_ID} .cgt-mt-crit { color: #FF453A; }
      #${PANEL_ID} .cgt-mt-miss { color: #8E8E93; }
      #${PANEL_ID} .cgt-mt-result-total small {
        font-size: 11px; color: #8e8e93; letter-spacing: 0.3px;
      }

      #${PANEL_ID} .cgt-mt-result-list {
        list-style: none; padding: 0; margin: 0;
        display: flex; flex-direction: column; gap: 8px;
      }
      #${PANEL_ID} .cgt-mt-result-row {
        padding: 12px 14px; border-radius: 10px;
        background: rgba(255,255,255,0.02);
        border: 0.5px solid rgba(255,255,255,0.06);
      }
      #${PANEL_ID} .cgt-mt-row-correct { border-left: 3px solid #30D158; }
      #${PANEL_ID} .cgt-mt-row-over { border-left: 3px solid #FF9F0A; }
      #${PANEL_ID} .cgt-mt-row-sub { border-left: 3px solid #FF453A; background: rgba(255,69,58,0.05); }
      #${PANEL_ID} .cgt-mt-row-missed { border-left: 3px solid #8E8E93; opacity: 0.7; }
      #${PANEL_ID} .cgt-mt-result-head {
        display: flex; align-items: center; gap: 8px; margin-bottom: 6px;
      }
      #${PANEL_ID} .cgt-mt-result-head strong { font-size: 13px; }
      #${PANEL_ID} .cgt-mt-result-head small {
        flex: 1; font-size: 11px; color: #8e8e93;
      }
      #${PANEL_ID} .cgt-mt-pts {
        font-family: 'JetBrains Mono', monospace;
        font-size: 13px; font-weight: 700;
      }
      #${PANEL_ID} .cgt-mt-row-correct .cgt-mt-pts { color: #30D158; }
      #${PANEL_ID} .cgt-mt-row-over .cgt-mt-pts { color: #FF9F0A; }
      #${PANEL_ID} .cgt-mt-row-sub .cgt-mt-pts { color: #FF453A; }
      #${PANEL_ID} .cgt-mt-row-missed .cgt-mt-pts { color: #8E8E93; }
      #${PANEL_ID} .cgt-mt-rationale {
        font-size: 11.5px; color: #a1a1a6; line-height: 1.5;
        padding: 6px 0;
      }
      #${PANEL_ID} .cgt-mt-rationale strong { color: #d1d1d6; }
      #${PANEL_ID} .cgt-mt-diff {
        font-size: 11px; color: #8e8e93; line-height: 1.4;
        padding-top: 4px; border-top: 0.5px solid rgba(255,255,255,0.04);
      }

      /* Debrief IA */
      #${PANEL_ID} .cgt-mt-debrief-holder { margin-top: 4px; }
      #${PANEL_ID} .cgt-mt-debrief-loading {
        padding: 16px 20px; text-align: center;
        background: rgba(41,151,255,0.06);
        border: 0.5px solid rgba(41,151,255,0.25);
        border-radius: 12px; color: #2997FF;
        font-size: 12.5px;
      }
      #${PANEL_ID} .cgt-mt-debrief-error {
        padding: 14px; background: rgba(255,69,58,0.08);
        border-radius: 10px; color: #FF6961; font-size: 12px;
      }
      #${PANEL_ID} .cgt-mt-debrief-card {
        background: linear-gradient(135deg, rgba(41,151,255,0.08), rgba(191,90,242,0.08));
        border: 0.5px solid rgba(41,151,255,0.3);
        border-radius: 14px; padding: 20px 24px;
        display: flex; flex-direction: column; gap: 14px;
      }
      #${PANEL_ID} .cgt-mt-debrief-head {
        display: flex; align-items: center; gap: 10px;
      }
      #${PANEL_ID} .cgt-mt-debrief-badge {
        padding: 5px 12px; border-radius: 12px;
        background: linear-gradient(135deg, #2997FF, #BF5AF2);
        color: #fff; font-size: 11px; font-weight: 700;
        letter-spacing: 0.3px;
      }
      #${PANEL_ID} .cgt-mt-debrief-summary {
        font-size: 13.5px; color: #f5f5f7; line-height: 1.55;
        margin: 0;
      }
      #${PANEL_ID} .cgt-mt-debrief-section h4 {
        font-size: 11px; font-weight: 700; letter-spacing: 0.8px;
        color: #a1a1a6; text-transform: uppercase; margin-bottom: 6px;
      }
      #${PANEL_ID} .cgt-mt-debrief-section ul {
        list-style: none; padding: 0; margin: 0;
        display: flex; flex-direction: column; gap: 4px;
      }
      #${PANEL_ID} .cgt-mt-debrief-section li {
        padding: 6px 10px;
        background: rgba(255,255,255,0.03);
        border-radius: 8px; font-size: 12px; color: #d1d1d6; line-height: 1.5;
      }

      #${PANEL_ID} .cgt-mt-result-actions {
        display: flex; justify-content: center; gap: 10px;
      }
      #${PANEL_ID} .cgt-mt-btn-primary, #${PANEL_ID} .cgt-mt-btn-secondary {
        padding: 12px 24px; border-radius: 10px; font-weight: 700; font-size: 14px;
        font-family: inherit; cursor: pointer; border: none;
      }
      #${PANEL_ID} .cgt-mt-btn-primary {
        background: linear-gradient(135deg, #2997FF, #BF5AF2); color: #fff;
      }
      #${PANEL_ID} .cgt-mt-btn-secondary {
        background: rgba(255,255,255,0.06); color: #a1a1a6;
        border: 0.5px solid rgba(255,255,255,0.1);
      }
    `;
    document.head.appendChild(style);
  }
}

function esc(text: string): string {
  return text.replace(/[&<>"']/g, c => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c] ?? c));
}

export const massTriageRoom = new MassTriageRoom();
